/**
 * QueVee tool-bridge client.
 *
 * Speaks the versioned envelope protocol to the QueVee agent middleware:
 * registers a client-side tool manifest on connect, streams assistant text out
 * to the chat UI, and executes `tool_call`s locally against whatever the host
 * app registered — posting each outcome back as a `tool_result`.
 *
 * The client is deliberately tool-agnostic. It never imports the workspace
 * model or the chart engine: the *host* decides what `addPane` means and hands
 * that in as a {@link ClientTool}. That keeps this package reusable by the web
 * app today and by any other TS host later.
 *
 * The WebSocket is injectable (same seam as `@qufolio/transport`), so the whole
 * protocol is unit-testable without a server.
 */

import { mintUlid, validateEnvelope, type SessionEnd, type ToolBridgeEnvelope } from "@qufolio/model";

import {
  sessionInitEnvelope,
  toolErrorEnvelope,
  toolResultEnvelope,
  userMessageEnvelope,
  type OutboundEnvelope,
  type ToolManifestEntry,
} from "./envelope.js";

/** Connection lifecycle status. Mirrors `@qufolio/transport` for consistency. */
export type ConnectionStatus = "idle" | "connecting" | "open" | "closed";

/**
 * What a tool handler may return. Results travel as JSON objects, so a tool
 * with a scalar answer wraps it (`{ symbol: "AAPL" }`); returning nothing is a
 * bare success, which is the common case for workspace mutations.
 */
export type ToolResultBody = Record<string, unknown> | void;

/** A locally-executable tool offered to the agent. */
export interface ClientTool {
  /** Tool name as the agent will call it (e.g. `"addPane"`). */
  name: string;
  /** What the tool does — this is prompt text; the agent picks tools by it. */
  description: string;
  /** JSON Schema for the arguments object. */
  inputSchema: Record<string, unknown>;
  /**
   * Executes the tool. Throwing is a normal outcome: the message comes back to
   * the agent as a tool error so it can correct itself and retry.
   */
  handler: (args: Record<string, unknown>) => Promise<ToolResultBody> | ToolResultBody;
}

/** An error surfaced to listeners. */
export interface QueVeeError {
  /** Where the failure originated. */
  source: "socket" | "protocol" | "server";
  message: string;
  /** Machine-readable code, when the server supplied one. */
  code?: string;
  cause?: unknown;
}

/** A tool call as received from the server, before local dispatch. */
export interface DispatchedToolCall {
  toolCallId: string;
  name: string;
  arguments: Record<string, unknown>;
}

/** The outcome of a locally-dispatched tool call. */
export interface DispatchedToolOutcome extends DispatchedToolCall {
  isError: boolean;
  result?: Record<string, unknown>;
  errorMessage?: string;
}

/** Callback bundle registered on a client. All handlers are optional. */
export interface QueVeeListeners {
  /** Fired for each assistant text chunk. */
  onTextDelta?(delta: { text: string; isFinal: boolean }): void;
  /** Fired when a tool call arrives, before the handler runs. */
  onToolCall?(call: DispatchedToolCall): void;
  /** Fired after a handler settles, for successes and failures alike. */
  onToolResult?(outcome: DispatchedToolOutcome): void;
  /**
   * Fired when the server signals the assistant turn is complete, with what it
   * was stored as and what it cost when the server says (`SessionEnd`) —
   * absent on a turn refused before it ran, and from an older server.
   */
  onTurnEnd?(end?: SessionEnd): void;
  /** Fired on socket / protocol / server errors. */
  onError?(error: QueVeeError): void;
  /** Fired on connection status transitions. */
  onStatus?(status: ConnectionStatus): void;
}

// ── Injectable seams (keep the client unit-testable) ─────────────────────────

/** The minimal subset of the browser `WebSocket` this client depends on. */
export interface WebSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

/** Factory that mints a {@link WebSocketLike}; defaults to the global `WebSocket`. */
export type WebSocketFactory = (url: string) => WebSocketLike;

/** Timer seam so reconnect backoff is testable without real delays. */
export type TimerFn = (fn: () => void, ms: number) => unknown;

/** Reconnect backoff configuration. */
export interface ReconnectOptions {
  /** Set false to never reconnect automatically. Default true. */
  enabled?: boolean;
  /** First retry delay in ms. Default 500. */
  initialDelayMs?: number;
  /** Delay ceiling in ms. Default 30_000. */
  maxDelayMs?: number;
  /** Give up after this many consecutive failures. Default Infinity. */
  maxAttempts?: number;
}

/** Options for {@link QueVeeClient}. */
export interface QueVeeClientOptions {
  /** QueVee WebSocket URL, e.g. `"wss://quevee.fly.dev/ws/chat"`. */
  url: string;

  /** Client-side tools offered to the agent. */
  tools: ClientTool[];

  /** Listener callbacks. */
  listeners?: QueVeeListeners;

  /**
   * Firebase ID token, sent as the socket's **first frame**.
   *
   * `{"type":"auth","token":"..."}`, which is the same handshake qv-data's
   * sockets read — one spelling across both services, so a client has one
   * handshake rather than two.
   *
   * It used to be an `access_token` query parameter, and **quevee never read
   * it**: the token went into every access and proxy log, and into browser
   * history, in exchange for nothing. A browser cannot set `Authorization` on a
   * WebSocket handshake, so a first frame is the remaining option and the one
   * `docs/plans/security.md` specifies.
   */
  idToken?: string;

  /**
   * The auth frame's contents, asked for **on every open** — in place of the
   * fixed `idToken` when both are given.
   *
   * A token minted when the client was built is an hour from expiring, and a
   * reconnect after that sends a dead one: the server closes 4401 and the
   * backoff retries the same dead token for ever. Asked per open, every
   * connection carries a current token, and `pass` (the signed groups pass,
   * `X-QF-Pass` over HTTP) lets the server skip its own entitlement lookup.
   * Frames sent while this resolves are queued, so nothing overtakes the auth
   * frame. Resolving `null` sends no auth frame — an anonymous socket.
   */
  auth?: () => Promise<{ token: string; pass?: string } | null>;

  /** External id of the workspace this session may mutate. */
  workspaceId?: string;

  /**
   * The saved conversation to continue. Absent starts a new one; the server
   * reports the id it stored a new chat under in `SessionEnd.conversationId`,
   * and the client keeps it — so a reconnect continues that conversation
   * rather than starting another. Read it back with `conversation`.
   */
  conversationId?: string;

  /**
   * Stable chat session id, carried on every frame so the server can pin the
   * session to one machine. Minted if omitted.
   */
  sessionId?: string;

  /** Reconnect behaviour. */
  reconnect?: ReconnectOptions;

  /** Inject a WebSocket implementation (defaults to the global `WebSocket`). */
  webSocketFactory?: WebSocketFactory;

  /** Inject a timer (defaults to the global `setTimeout`). */
  setTimeoutFn?: TimerFn;
}

function defaultWebSocketFactory(url: string): WebSocketLike {
  const Ctor = (globalThis as { WebSocket?: unknown }).WebSocket;
  if (typeof Ctor !== "function") {
    throw new Error("No global WebSocket available; pass options.webSocketFactory.");
  }
  return new (Ctor as new (u: string) => WebSocketLike)(url);
}

/**
 * WebSocket client for the QueVee agent middleware.
 *
 * Lifecycle: `connect()` opens the socket and sends `session_init` with the
 * tool manifest; `send()` posts a user turn; inbound `tool_call`s are dispatched
 * to the matching {@link ClientTool} and answered with a `tool_result`.
 */
export class QueVeeClient {
  private readonly options: QueVeeClientOptions;
  private readonly toolsByName: Map<string, ClientTool>;
  private readonly sessionId: string;
  /** The saved conversation — given, or learned from the first `session_end`. */
  private conversationId: string | undefined;

  private ws: WebSocketLike | null = null;
  private _status: ConnectionStatus = "idle";

  /** Frames accepted while the socket was down; flushed in order on open. */
  private readonly outbox: OutboundEnvelope[] = [];

  /** True once `disconnect()` was called — suppresses automatic reconnect. */
  private closedByCaller = false;
  private reconnectAttempts = 0;

  constructor(options: QueVeeClientOptions) {
    this.options = options;
    this.toolsByName = new Map(options.tools.map((t) => [t.name, t]));
    this.sessionId = options.sessionId ?? mintUlid();
    this.conversationId = options.conversationId;
  }

  /** The saved conversation this client continues, once there is one. */
  get conversation(): string | undefined {
    return this.conversationId;
  }

  /** Current lifecycle status. */
  get status(): ConnectionStatus {
    return this._status;
  }

  /** The session id carried on every frame (minted if not supplied). */
  get session(): string {
    return this.sessionId;
  }

  /**
   * Open the socket. Resolves once `session_init` has been sent, so callers may
   * `send()` immediately afterwards.
   */
  connect(): Promise<void> {
    if (this.ws && (this._status === "open" || this._status === "connecting")) {
      return Promise.resolve();
    }
    this.closedByCaller = false;
    this.setStatus("connecting");

    const factory = this.options.webSocketFactory ?? defaultWebSocketFactory;
    const ws = factory(this.buildUrl());
    this.ws = ws;

    return new Promise<void>((resolve, reject) => {
      // A refused handshake may only ever fire `onclose`, so settle on both
      // paths — otherwise `await connect()` hangs forever.
      let settled = false;

      const open = (auth: { token: string; pass?: string } | null) => {
        // A socket replaced or closed while the token was being minted.
        if (this.ws !== ws) return;
        this.reconnectAttempts = 0;
        this._status = "open";
        // **The auth frame leads, ahead of the manifest.** The server reads the
        // first frame and classifies it: an `auth` frame authenticates and is
        // consumed, anything else is treated as anonymous and passed on to the
        // protocol layer. Sent on every connection including reconnects, for
        // the same reason the manifest is — the server-side session is fresh
        // and knows nothing about the last one.
        //
        // Sent raw rather than through `transmit`, because it is not an
        // envelope: it precedes the protocol rather than being part of it, and
        // queueing it in the outbox would let it arrive after `session_init`.
        if (auth?.token) {
          ws.send(JSON.stringify({ type: "auth", token: auth.token, ...(auth.pass ? { pass: auth.pass } : {}) }));
        }
        // The manifest must lead every connection, including reconnects —
        // a fresh server-side session knows nothing about our tools. It names
        // the conversation too, so a reconnect continues the same one.
        this.transmit(
          sessionInitEnvelope(this.manifest(), {
            sessionId: this.sessionId,
            workspaceId: this.options.workspaceId,
            conversationId: this.conversationId,
          }),
        );
        this.flushOutbox();
        this.options.listeners?.onStatus?.("open");
        settled = true;
        resolve();
      };
      ws.onopen = () => {
        const ask = this.options.auth;
        if (!ask) {
          open(this.options.idToken ? { token: this.options.idToken } : null);
          return;
        }
        // Status stays `connecting` until the frame is out, so a `send()` in
        // the meantime is queued behind it rather than overtaking it.
        void ask().then(open, () => open(null));
      };
      ws.onmessage = (ev) => {
        void this.handleMessage(ev.data);
      };
      ws.onclose = () => {
        this.ws = null;
        this.setStatus("closed");
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket closed before it opened"));
        }
        this.scheduleReconnect();
      };
      ws.onerror = (cause) => {
        this.emitError({ source: "socket", message: "WebSocket error", cause });
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket error"));
        }
      };
    });
  }

  /**
   * Send one user chat turn. If the socket is not open the frame is queued and
   * flushed on the next successful connect, so a reconnect gap doesn't drop the
   * user's message.
   */
  send(text: string, context?: Record<string, unknown>): void {
    this.transmit(
      userMessageEnvelope(text, {
        sessionId: this.sessionId,
        messageId: mintUlid(),
        ...(context ? { context } : {}),
      }),
    );
  }

  /** Close the socket and stop reconnecting. Queued frames are discarded. */
  disconnect(): void {
    this.closedByCaller = true;
    this.outbox.length = 0;
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.close();
    }
    this.setStatus("closed");
  }

  /** The wire form of the registered tools (handlers stripped). */
  private manifest(): ToolManifestEntry[] {
    return this.options.tools.map((t) => ({
      name: t.name,
      description: t.description,
      inputSchema: t.inputSchema,
    }));
  }

  // ── internals ───────────────────────────────────────────────────────────

  /** The socket URL. The token is **not** in it — see `idToken`. */
  private buildUrl(): string {
    return this.options.url;
  }

  /** Send now if open, otherwise queue for the next connect. */
  private transmit(envelope: OutboundEnvelope): void {
    if (this.ws && this._status === "open") {
      this.ws.send(JSON.stringify(envelope));
      return;
    }
    this.outbox.push(envelope);
  }

  private flushOutbox(): void {
    if (!this.ws) return;
    const pending = this.outbox.splice(0, this.outbox.length);
    for (const envelope of pending) this.ws.send(JSON.stringify(envelope));
  }

  private async handleMessage(data: unknown): Promise<void> {
    if (typeof data !== "string") {
      this.emitError({
        source: "protocol",
        message: "Expected a text frame on the tool bridge",
      });
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch (cause) {
      this.emitError({ source: "protocol", message: "Malformed JSON frame", cause });
      return;
    }

    // The server is the untrusted edge here: validate before acting on it.
    const { valid, data: envelope, errors } = validateEnvelope(parsed);
    if (!valid || !envelope) {
      this.emitError({
        source: "protocol",
        message: `Invalid envelope: ${errors.join("; ")}`,
      });
      return;
    }

    await this.dispatch(envelope);
  }

  private async dispatch(envelope: ToolBridgeEnvelope): Promise<void> {
    switch (envelope.type) {
      case "text_delta":
        this.options.listeners?.onTextDelta?.({
          text: envelope.payload.text,
          isFinal: envelope.payload.isFinal ?? false,
        });
        return;

      case "tool_call":
        await this.runTool(envelope.payload);
        return;

      case "session_end":
        // Kept before the listener runs, so a reconnect the listener provokes
        // already names the conversation the turn was stored in.
        if (envelope.payload?.conversationId) this.conversationId = envelope.payload.conversationId;
        this.options.listeners?.onTurnEnd?.(envelope.payload);
        return;

      case "error":
        this.emitError({
          source: "server",
          code: envelope.payload.code,
          message: envelope.payload.message,
        });
        return;

      // Client→server frames are not expected back from the server.
      case "session_init":
      case "user_message":
      case "tool_result":
        this.emitError({
          source: "protocol",
          message: `Unexpected inbound frame of type '${envelope.type}'`,
        });
    }
  }

  private async runTool(call: {
    toolCallId: string;
    name: string;
    arguments: Record<string, unknown>;
  }): Promise<void> {
    const args = (call.arguments ?? {}) as Record<string, unknown>;
    this.options.listeners?.onToolCall?.({
      toolCallId: call.toolCallId,
      name: call.name,
      arguments: args,
    });

    const tool = this.toolsByName.get(call.name);
    if (!tool) {
      // Name the registered tools so the agent can retry with a real one
      // instead of guessing again.
      const known = [...this.toolsByName.keys()].join(", ") || "(none)";
      this.failTool(call, args, `Unknown client tool '${call.name}'. Registered: ${known}`);
      return;
    }

    try {
      const result = await tool.handler(args);
      const body = result ?? undefined;
      this.transmit(
        toolResultEnvelope(call.toolCallId, body, { sessionId: this.sessionId }),
      );
      this.options.listeners?.onToolResult?.({
        toolCallId: call.toolCallId,
        name: call.name,
        arguments: args,
        isError: false,
        ...(body ? { result: body } : {}),
      });
    } catch (cause) {
      this.failTool(call, args, cause instanceof Error ? cause.message : String(cause));
    }
  }

  private failTool(
    call: { toolCallId: string; name: string },
    args: Record<string, unknown>,
    errorMessage: string,
  ): void {
    this.transmit(
      toolErrorEnvelope(call.toolCallId, errorMessage, { sessionId: this.sessionId }),
    );
    this.options.listeners?.onToolResult?.({
      toolCallId: call.toolCallId,
      name: call.name,
      arguments: args,
      isError: true,
      errorMessage,
    });
  }

  private scheduleReconnect(): void {
    const cfg = this.options.reconnect ?? {};
    if (this.closedByCaller || cfg.enabled === false) return;

    const maxAttempts = cfg.maxAttempts ?? Number.POSITIVE_INFINITY;
    if (this.reconnectAttempts >= maxAttempts) return;

    const initial = cfg.initialDelayMs ?? 500;
    const max = cfg.maxDelayMs ?? 30_000;
    const delay = Math.min(initial * 2 ** this.reconnectAttempts, max);
    this.reconnectAttempts += 1;

    const timer = this.options.setTimeoutFn ?? setTimeout;
    timer(() => {
      if (this.closedByCaller) return;
      // A failed retry lands back in onclose/onerror, which re-schedules.
      void this.connect().catch(() => undefined);
    }, delay);
  }

  private emitError(error: QueVeeError): void {
    this.options.listeners?.onError?.(error);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.options.listeners?.onStatus?.(status);
  }
}
